import { useDispatch, useSelector } from 'react-redux';
import { COUNTRIES } from '@wareclouds/frontend-common';
import {
  CONTEXT,
  IS_BR_CONTEXT,
  PATH_BANK_ACCOUNT,
} from '@/config/consts/commons';
import { useEffect, useMemo } from 'react';
import { AlertComponent } from '@/features/common/components';
import { Trans } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useGetBankAccountInfo } from '@/features/common/hooks/useGetBankAccountInfo';
import { setHasBankData } from '@/features/auth/utils/loginSlice';
import useCanAccessBankAccount from './useCanAccessBankAccount';

const useHasMissingBankDetails = () => {
  const hasBankData = useSelector(({ auth }) => auth.hasBankData);
  const location = useLocation();
  const { username } = useSelector((state) => state.auth);
  const isBankAccountRoute = location.pathname === PATH_BANK_ACCOUNT;
  const { canAccessBankAccount, isLoading: isLoadingBankAccess } =
    useCanAccessBankAccount();

  const dispatch = useDispatch();

  const {
    data = {},
    isLoading,
    isFetching,
  } = useGetBankAccountInfo({
    userId: username,
    userType: 'clouder',
    enabled: !isLoadingBankAccess && canAccessBankAccount,
  });

  useEffect(() => {
    if (!canAccessBankAccount) {
      return;
    }

    const hasBrBankData = Object.keys(data?.accounts ?? {}).length > 0;
    const hasClBankData = Boolean(
      data?.accounts?.RUT?.id_user_bank_account || data?.id_user_bank_account
    );

    dispatch(setHasBankData(IS_BR_CONTEXT ? hasBrBankData : hasClBankData));
  }, [canAccessBankAccount, data, dispatch]);

  const MessageHasNotBankData = useMemo(() => {
    return function MessageHasNotBankData() {
      const isSupportedCountry = [COUNTRIES.CL, COUNTRIES.BR].includes(CONTEXT);
      const shouldShow =
        canAccessBankAccount &&
        isSupportedCountry &&
        !hasBankData &&
        !isLoading &&
        !isFetching;

      if (!shouldShow) {
        return null;
      }

      return (
        <AlertComponent severity="warning" variant="filled">
          <Trans i18nKey="BANK_INFORMATION_REQUIRED" ns="system">
            <Link
              to="/profile/bank-account"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            />
          </Trans>
        </AlertComponent>
      );
    };
  }, [canAccessBankAccount, hasBankData, isLoading, isFetching]);

  return {
    hasBankData,
    isBankAccountRoute,
    MessageHasNotBankData,
  };
};

export default useHasMissingBankDetails;
